// The Cyber Essentials / Cyber Essentials Plus requirements for IT infrastructure, in the five
// technical themes, with the guidance the page shows. Each key below ("fw_boundary") is the id of a
// Cyber Essentials Control row (backend lib/ceRequirements.ts, the seeded copy; ceRequirementsSync.test.ts
// keeps the two identical), shared by CE and CE+, and the title carries the requirement's own reference
// ("A4.1 — …"), which lib/controlRef.ts reads so a tag never shows the bare key. Pure data.

export interface ControlDef {
  key: string;
  title: string;
  description: string;
  guidance: string[];
}

export interface ThemeDef {
  id: string;
  title: string;
  controls: ControlDef[];
}

export const CONTROL_THEMES: ThemeDef[] = [
  {
    id: "firewalls",
    title: "A4 — Firewalls",
    controls: [
      {
        key: "fw_boundary",
        title: "A4.1 — Boundary firewall in place at every network perimeter",
        description:
          "Every network the organisation is responsible for has a correctly configured firewall (or firewall-capable network device) at its boundary.",
        guidance: [
          "Ensure a firewall or firewall-capable router sits at the boundary of every network you manage",
          "Confirm the firewall is active and correctly configured — not just installed",
          "This applies to office networks, cloud virtual networks, and any perimeter the org controls",
          "BYOD and home networks are out of scope for boundary firewalls; use software firewalls instead (A4.1.1)",
        ],
      },
      {
        key: "fw_software_fw",
        title: "A4.1.1 — Software firewall enabled on all computers, laptops, and servers",
        description:
          "All computers, laptops, and servers in scope have a software (host-based) firewall switched on.",
        guidance: [
          "Enable the built-in OS firewall on every Windows, macOS, and Linux device",
          "Enforce via group policy, MDM, or configuration management so users cannot disable it",
          "Include servers — not just end-user devices",
          "Remote workers' personal devices used for business must also have a software firewall active",
        ],
      },
      {
        key: "fw_default_pw",
        title: "A4.2 — Default passwords changed on all boundary firewall devices",
        description:
          "All default administrative passwords on boundary firewall devices have been changed to strong, unique passwords before the device was put into service.",
        guidance: [
          "Change the default admin password on every router, firewall, and network boundary device",
          "Use a strong, unique password for each device — never reuse across devices",
          "Store credentials in an approved, encrypted password manager",
          "If the default username is changeable, change that too",
        ],
      },
      {
        key: "fw_pw_strength",
        title: "A4.3 — Firewall admin passwords meet strength requirement",
        description:
          "Administrative access to firewalls meets at least one of: MFA required; passwords ≥8 characters checked against a common-password blocklist; passwords ≥12 characters.",
        guidance: [
          "Option A: Enable MFA for all firewall admin interfaces",
          "Option B: Enforce passwords of at least 8 characters AND check against a blocklist of common/breached passwords",
          "Option C: Enforce passwords of at least 12 characters with no maximum length",
          "Passwordless authentication (FIDO2, hardware key) is accepted as equivalent to MFA",
        ],
      },
      {
        key: "fw_compromised_pw",
        title: "A4.4 — Process to change compromised firewall passwords promptly",
        description:
          "A documented process exists to change firewall admin passwords promptly if compromise is suspected or confirmed.",
        guidance: [
          "Document the steps to change firewall admin credentials when compromise is suspected",
          "Define 'promptly' — NCSC guidance implies hours, not days",
          "Include escalation contacts and who is authorised to initiate the change",
          "Test the process at least annually so staff know what to do under pressure",
        ],
      },
      {
        key: "fw_rules_process",
        title: "A4.5 — Firewall rules management process with business-case approval",
        description:
          "A process exists to manage firewall rules, requiring a documented business case each time a new port or service is opened.",
        guidance: [
          "Document every firewall rule, including the authorising person and the business need",
          "Require written approval from an authorised person before any new rule is added",
          "Rules with no documented business case must be removed or disabled",
          "Log all rule changes with a timestamp and reason",
        ],
      },
      {
        key: "fw_rules_reviewed",
        title: "A4.6 — All firewall rules reviewed in the last 12 months",
        description:
          "A review of all firewall rules has been completed within the past 12 months, and rules no longer needed have been removed or disabled.",
        guidance: [
          "Conduct a full audit of every active firewall rule at least once per year",
          "Remove or disable any rule that no longer has a current business justification",
          "Document the review date and who performed it",
          "Schedule the next review before the current one expires",
        ],
      },
      {
        key: "fw_block_inbound",
        title: "A4.7 — Firewall blocks unauthenticated inbound connections by default",
        description:
          "Firewalls are configured to block all unauthenticated inbound connections from the internet unless explicitly permitted.",
        guidance: [
          "Confirm the default rule on all firewalls is 'deny all inbound'",
          "Only explicitly whitelisted, business-justified connections should be allowed through",
          "Cover both IPv4 and IPv6 traffic",
          "Check this applies to boundary firewalls AND software firewalls on individual devices",
        ],
      },
      {
        key: "fw_inbound_documented",
        title: "A4.8 — All allowed inbound connections approved and documented",
        description:
          "Every permitted inbound connection through the firewall has been approved by an authorised person and the business need is recorded.",
        guidance: [
          "Maintain a register of all inbound firewall rules with the approver name and business justification",
          "Review the register whenever rules change",
          "Cross-reference the register against actual firewall rule sets to ensure they match",
          "Remove approvals that are outdated or where the business need no longer exists",
        ],
      },
      {
        key: "fw_no_internet_admin",
        title: "A4.9 — No internet access to firewall admin interface (unless protected)",
        description:
          "Boundary firewalls are not configured to allow admin access over the internet, unless that access is protected by MFA or an IP allowlist combined with managed authentication.",
        guidance: [
          "Block all internet-facing access to firewall admin/management interfaces by default",
          "If remote admin access is required, protect it with MFA or a strict IP allowlist",
          "IP allowlist addresses must be static and managed — not end-user home IPs",
          "Confirm this is in effect — do not rely on 'we don't have remote access' without verification",
        ],
      },
      {
        key: "fw_remote_protected",
        title: "A4.10/11 — Remote admin access documented and protected (if applicable)",
        description:
          "If remote administrative access to firewall configuration exists, it is covered by a documented business requirement and is protected by MFA or an IP allowlist with managed authentication.",
        guidance: [
          "Document the business reason for any remote admin access to firewall configuration",
          "Protect all such access with MFA or an approved IP allowlist",
          "Review the documented business requirement at least annually",
          "If no remote admin access exists, record this as N/A and mark compliant",
        ],
      },
    ],
  },
  {
    id: "secure_config",
    title: "A5 — Secure Configuration",
    controls: [
      {
        key: "sc_remove_software",
        title: "A5.1 — Unnecessary software, applications, and services removed",
        description:
          "All unnecessary software, applications, system utilities, and network services have been removed or disabled from in-scope devices.",
        guidance: [
          "Audit all installed software and running services on every device in scope",
          "Remove or disable anything not needed for business operations",
          "Disable OS features and background services that are not required",
          "Remove browser extensions, plugins, and toolbars not explicitly approved",
        ],
      },
      {
        key: "sc_remove_accounts",
        title: "A5.2 — Only necessary user accounts exist on devices and cloud services",
        description:
          "All unnecessary user accounts — including guest, default, and inactive accounts — have been removed or disabled on all in-scope devices and cloud services.",
        guidance: [
          "Audit all accounts on every device and cloud service",
          "Disable or remove guest accounts, default accounts, and any account with no current user",
          "Include shared accounts and service accounts in the review",
          "Perform this review at least quarterly",
        ],
      },
      {
        key: "sc_change_defaults",
        title: "A5.3 — Default passwords changed on all devices",
        description:
          "Default or guessable account passwords have been changed on every in-scope device before deployment, and password authentication meets the strength requirement.",
        guidance: [
          "Change all default passwords on servers, desktops, laptops, tablets, phones, and network devices",
          "Meet at least one of: MFA required; passwords ≥8 characters with blocklist; passwords ≥12 characters",
          "Never use guessable passwords such as the device brand, model, or 'password'",
          "Store all credentials in an approved, encrypted password manager",
        ],
      },
      {
        key: "sc_auth_required",
        title: "A5.4 — External-facing services require authentication before access to organisational data",
        description:
          "All services accessible from outside the organisation require users to authenticate before they can access organisational data or services.",
        guidance: [
          "List all services accessible from outside the organisation",
          "Confirm every one requires authentication before exposing organisational data",
          "Include web applications, remote desktop, VPNs, cloud portals, and APIs",
          "Unauthenticated public-facing content is acceptable only if it genuinely contains no organisational data",
        ],
      },
      {
        key: "sc_auth_config",
        title: "A5.5 — Authentication on external services meets strength requirement",
        description:
          "Authentication for external-facing services meets at least one of: passwordless authentication; MFA; passwords ≥8 characters with blocklist; passwords ≥12 characters.",
        guidance: [
          "Option A: Passwordless authentication (FIDO2, biometric, hardware security key)",
          "Option B: MFA required for all external access",
          "Option C: Passwords of at least 8 characters AND checked against a common-password blocklist",
          "Option D: Passwords of at least 12 characters with no maximum length",
        ],
      },
      {
        key: "sc_compromised_pw",
        title: "A5.6 — Process to change external service passwords when compromised",
        description:
          "A documented process exists to change passwords on external-facing services promptly when compromise is suspected or confirmed.",
        guidance: [
          "Document the steps for resetting credentials on every external-facing service",
          "Define escalation contacts and response timelines",
          "Include cloud services, remote access portals, and external-facing web applications",
          "Test the process periodically so staff know how to execute it quickly",
        ],
      },
      {
        key: "sc_brute_force",
        title: "A5.7 — Brute-force protection on all external-facing accounts",
        description:
          "All accounts on external-facing services are protected against brute-force password attacks through throttling or account lockout.",
        guidance: [
          "Configure account lockout after a maximum of 10 failed login attempts, OR",
          "Implement throttling that allows no more than 10 guesses per 5 minutes",
          "Apply this to every service accessible from outside the organisation",
          "MFA can substitute for brute-force protection if enabled on all accounts",
        ],
      },
      {
        key: "sc_autorun_off",
        title: "A5.8 — Auto-run disabled for downloaded or imported files",
        description:
          "Auto-run and auto-execute features are disabled on all devices so that files downloaded from the internet or imported from removable media cannot execute without user action.",
        guidance: [
          "Disable Windows AutoRun and AutoPlay for all media types and removable drives",
          "Configure browsers to prompt before opening downloaded files rather than opening automatically",
          "Apply via group policy or MDM so the setting cannot be changed by standard users",
          "Include email attachments in scope — confirm attachments do not auto-execute",
        ],
      },
      {
        key: "sc_device_lock",
        title: "A5.9 — Device locking mechanism in place on all devices",
        description:
          "All in-scope devices have a locking mechanism that activates after a period of inactivity and requires re-authentication to unlock.",
        guidance: [
          "Enable automatic screen lock on all desktops, laptops, tablets, and mobile devices",
          "Set the inactivity timeout appropriately — a short lock time reduces risk",
          "Enforce via policy so standard users cannot disable or extend the timeout",
          "Include servers and shared workstations in scope",
        ],
      },
      {
        key: "sc_lock_method",
        title: "A5.10 — Device unlock method configured with brute-force protection",
        description:
          "The method used to unlock devices (biometric, password, or PIN) includes protection against brute-force attacks.",
        guidance: [
          "Use biometric (fingerprint, face), password, or PIN as the unlock method",
          "Configure lockout or throttling so no more than 10 failed unlock attempts are permitted",
          "Minimum 6-digit PIN length applies; if the unlock credential doubles as a login credential, full password requirements apply",
          "Enforce via MDM or device management — do not rely on user configuration",
        ],
      },
    ],
  },
  {
    id: "update_management",
    title: "A6 — Security Update Management",
    controls: [
      {
        key: "um_os_supported",
        title: "A6.1 [AUTO-FAIL] — All operating systems are supported and receiving security updates",
        description:
          "Every operating system on every in-scope device is currently supported by the vendor and actively receiving security updates. A 'No' answer is an automatic assessment failure.",
        guidance: [
          "Check the end-of-support date for every OS version in use across all devices",
          "Upgrade or replace any device running an end-of-life OS before assessment",
          "This covers Windows, macOS, Linux, iOS, Android, and any other OS on in-scope devices",
          "A single unsupported OS instance fails the entire assessment — there are no exceptions",
        ],
      },
      {
        key: "um_sw_supported",
        title: "A6.2 — All software is supported and receiving vulnerability fixes",
        description:
          "All software on in-scope devices — including browsers, email clients, office applications, and security software — is currently supported and receiving vulnerability fixes from the supplier.",
        guidance: [
          "Check internet browsers (Chrome, Edge, Firefox, Safari) are on a supported, current version",
          "Verify anti-malware and security software is supported and updating",
          "Check email applications (Outlook, Thunderbird, Apple Mail) are current versions",
          "Verify office productivity software (Microsoft 365, LibreOffice, Google Workspace) is supported",
        ],
      },
      {
        key: "um_licensed_only",
        title: "A6.3 — No unlicensed or unsupported software on any device",
        description:
          "No unlicensed software or software that has reached end-of-support is present on any in-scope device or cloud service.",
        guidance: [
          "Audit software licences across all devices and confirm each is valid and current",
          "Remove any software for which the licence has lapsed or the vendor no longer supports it",
          "Use a software asset management (SAM) tool to track licence compliance continuously",
          "Include cloud software subscriptions — lapsed SaaS accounts still count",
        ],
      },
      {
        key: "um_os_patches_14d",
        title: "A6.4 [AUTO-FAIL] — High/critical OS patches applied within 14 days of release",
        description:
          "Any OS patch rated 'critical' or 'high' by the vendor, or with a CVSS v3 base score ≥ 7.0, is applied across all in-scope devices within 14 days of release. Auto-fail if not met.",
        guidance: [
          "Subscribe to OS vendor security bulletins (Microsoft, Apple, Linux distros) so patches are seen on release day",
          "Apply critical and high patches within 14 days — do not wait for the next scheduled maintenance window",
          "Enable automatic OS updates where possible (A6.4.1) so critical patches apply without manual intervention",
          "Where auto-updates are disabled, document the manual patching process (A6.4.2) and verify adherence",
        ],
      },
      {
        key: "um_app_patches_14d",
        title: "A6.5 [AUTO-FAIL] — High/critical application patches applied within 14 days",
        description:
          "Any application patch rated 'critical' or 'high', or with a CVSS v3 score ≥ 7.0, is applied to all in-scope applications within 14 days. Auto-fail if not met.",
        guidance: [
          "Subscribe to security advisories for all applications in scope (browsers, office tools, email clients)",
          "Apply critical/high application patches within 14 calendar days of release",
          "Enable automatic updates for browsers, productivity apps, and email clients where supported",
          "Log patch dates and keep records to demonstrate the 14-day requirement is met",
        ],
      },
      {
        key: "um_remove_unsupported",
        title: "A6.6 — Unsupported software removed from all devices",
        description:
          "Software that is no longer supported by its vendor has been removed from all in-scope devices.",
        guidance: [
          "Identify all software that has reached end-of-life or end-of-support",
          "Uninstall it from every device — simply disabling it is not sufficient",
          "If a business function still requires the software, replace it with a supported alternative",
          "Document the removal and the replacement solution implemented",
        ],
      },
      {
        key: "um_isolate_unsupported",
        title: "A6.7 — Unsupported software isolated to sub-set with no internet access (if applicable)",
        description:
          "Where unsupported software cannot be immediately removed for business reasons, it has been moved to a segregated network sub-set with no internet access.",
        guidance: [
          "This is an exceptional, temporary measure — plan to remove the software as soon as possible",
          "The sub-set must be segregated by a firewall or VLAN with no inbound or outbound internet traffic",
          "Document the business reason and the compensating control in place",
          "If no unsupported software is present, mark this control as Not Applicable / Compliant",
        ],
      },
    ],
  },
  {
    id: "user_access",
    title: "A7 — User Access Control",
    controls: [
      {
        key: "ua_account_process",
        title: "A7.1 — Formal process to create and approve user accounts",
        description:
          "A documented, formal process exists for requesting and approving the creation of user accounts before access is granted.",
        guidance: [
          "Document the steps from access request through to account creation",
          "Require written or tracked approval from a manager or system owner",
          "Apply the process to all accounts — employee, contractor, and third-party",
          "Audit adherence to the process at least annually",
        ],
      },
      {
        key: "ua_unique_credentials",
        title: "A7.2 — All users and admins use unique credentials — no shared accounts",
        description:
          "Every user and administrator authenticates with their own unique credentials. Generic, shared, or group accounts are not used.",
        guidance: [
          "Remove or disable all shared or generic accounts (e.g. 'admin', 'reception', 'IT')",
          "Assign a named individual account to every user, including temporary staff and contractors",
          "If a shared display or kiosk account is unavoidable, document the risk and controls applied",
          "Audit for shared accounts at least quarterly",
        ],
      },
      {
        key: "ua_disable_leavers",
        title: "A7.3 — Accounts disabled or deleted promptly when staff leave or change role",
        description:
          "When a member of staff leaves the organisation or changes role, their accounts are disabled or deleted, and their access privileges are removed promptly.",
        guidance: [
          "Integrate account disabling into the HR leaver process — ideally triggered on the last working day",
          "Include cloud services, email, remote access, and on-premises systems in scope",
          "For role changes, remove access that is no longer needed as soon as the change takes effect",
          "Audit for stale accounts (inactive for 90+ days) at least quarterly",
        ],
      },
      {
        key: "ua_least_privilege",
        title: "A7.4 — Staff have only the access privileges needed for their current role",
        description:
          "User accounts are granted only the minimum access privileges necessary for the individual's current job role, consistent with the principle of least privilege.",
        guidance: [
          "Review all account permissions and remove any that exceed what the current role requires",
          "Use role-based access control (RBAC) to manage permissions at scale",
          "Include file shares, databases, cloud services, and on-premises systems",
          "Review privileges when roles change, not just when users join or leave",
        ],
      },
      {
        key: "ua_admin_approval",
        title: "A7.5 — Formal process for granting administrative access",
        description:
          "A documented, formal process exists specifically for requesting, approving, and granting administrative access.",
        guidance: [
          "Maintain a separate process for admin access — distinct from standard account creation",
          "Require written approval from a senior manager or designated authority",
          "Log every instance of admin access being granted, with the approver and business justification",
          "Review the list of admin accounts at least annually",
        ],
      },
      {
        key: "ua_separate_admin",
        title: "A7.6 — Separate accounts used for administrative tasks",
        description:
          "Users who need to perform administrative tasks use a dedicated, separate admin account distinct from their standard user account.",
        guidance: [
          "Create a named, individual admin account for each IT administrator (e.g. 'admin.jsmith')",
          "The admin account should be used only for administrative tasks",
          "The administrator's day-to-day work should be done using their standard user account",
          "Avoid giving standard user accounts admin privileges — elevate temporarily if needed via PAM tools",
        ],
      },
      {
        key: "ua_no_admin_email",
        title: "A7.7 — Admin accounts not used for email or web browsing",
        description:
          "Administrative accounts are not used for everyday tasks such as reading email or browsing the web.",
        guidance: [
          "Confirm admin accounts do not have email clients configured or access to web browsers for general use",
          "Apply technical controls (e.g. browser restrictions) to admin accounts where possible",
          "Educate administrators on the risk of using elevated accounts for routine tasks",
          "Audit admin account activity for signs of email or browsing use",
        ],
      },
      {
        key: "ua_admin_tracking",
        title: "A7.8 — Record maintained of all users who hold administrative accounts",
        description:
          "An up-to-date record is kept of every user who holds an administrative account, and the record is reviewed regularly.",
        guidance: [
          "Maintain a register of admin accounts including the account name, the individual, and the system",
          "Update the register whenever admin accounts are created, changed, or removed",
          "Cross-reference the register against actual admin group membership at least quarterly",
          "Investigate and remediate any admin accounts not in the register",
        ],
      },
      {
        key: "ua_admin_review",
        title: "A7.9 — Admin access regularly reviewed and removed when no longer needed",
        description:
          "Administrative access privileges are reviewed regularly and removed or disabled as soon as they are no longer required.",
        guidance: [
          "Review all admin accounts at least annually — more frequently for high-privilege or cloud admin roles",
          "Remove admin access when the role, project, or business need no longer exists",
          "Include contractors and third-party administrators in the review",
          "Document the review findings and any actions taken",
        ],
      },
      {
        key: "ua_brute_force",
        title: "A7.10 — Accounts protected against brute-force password attacks",
        description:
          "All accounts are protected against brute-force password guessing through at least one of: MFA; throttling (max 10 guesses per 5 minutes); account lockout after max 10 failed attempts.",
        guidance: [
          "Option A: Enable MFA — effectively prevents brute-force at the password level",
          "Option B: Implement account lockout after no more than 10 consecutive failed attempts",
          "Option C: Implement throttling that restricts guesses to no more than 10 per 5-minute window",
          "Apply to every account — on-premises, cloud, VPN, and external-facing services",
        ],
      },
      {
        key: "ua_pw_quality",
        title: "A7.11 — Technical controls enforce password quality",
        description:
          "Technical controls enforce password quality for all accounts through at least one of: MFA required; minimum 12-character passwords; minimum 8-character passwords checked against a blocklist.",
        guidance: [
          "Option A: MFA required for all accounts — removes reliance on password complexity alone",
          "Option B: System enforces a minimum password length of 12 characters",
          "Option C: System enforces minimum 8 characters AND automatically blocks passwords from a common-password blocklist",
          "Apply consistently across all user and admin accounts — not just a subset",
        ],
      },
      {
        key: "ua_pw_support",
        title: "A7.12 — Users supported in choosing unique and strong passwords",
        description:
          "The organisation takes active steps to help users choose unique, strong passwords, following NCSC guidance on password hygiene.",
        guidance: [
          "Educate users on avoiding common passwords and reusing passwords across accounts",
          "Encourage three random words passphrases (NCSC 'three random words' approach)",
          "Provide or recommend an approved password manager so users can use unique passwords easily",
          "Do NOT enforce regular password expiry or complex character-type rules — these reduce security in practice",
        ],
      },
      {
        key: "ua_compromised_process",
        title: "A7.13 — Process to report and respond to compromised passwords or accounts",
        description:
          "A documented process exists for users to report suspected account compromise, and for the organisation to respond promptly, including disabling affected accounts.",
        guidance: [
          "Publish a clear, easy-to-find way for users to report suspected account compromise (e.g. IT helpdesk, security team email)",
          "Document the response steps: verify, disable account, reset credentials, investigate scope",
          "Aim to disable a compromised account within hours, not days",
          "Include the process in security awareness training so all staff know what to do",
        ],
      },
      {
        key: "ua_cloud_mfa_available",
        title: "A7.14 — MFA available on all cloud services used by the organisation",
        description:
          "MFA is available (offered as a feature) on every cloud service used by the organisation to store or process organisational data.",
        guidance: [
          "Inventory all cloud services in use, including shadow IT discovered through procurement or expense records",
          "Confirm each service offers MFA as an authentication option",
          "Where a cloud service does not offer MFA, assess whether it can be replaced or whether compensating controls are sufficient",
          "Note: A7.16 and A7.17 require MFA to actually be enabled — A7.14 asks whether it is available",
        ],
      },
      {
        key: "ua_cloud_admin_mfa",
        title: "A7.16 [AUTO-FAIL] — MFA applied to all cloud service administrator accounts",
        description:
          "MFA is enabled and enforced on every administrator account for every in-scope cloud service. A 'No' answer is an automatic assessment failure.",
        guidance: [
          "Enable and enforce MFA on every admin account in Microsoft 365, Google Workspace, Azure, AWS, and all other cloud platforms",
          "Use Conditional Access (Microsoft) or equivalent policies to block admin login without MFA",
          "Remove exceptions or break-glass accounts from admin roles — or protect them with equivalent controls",
          "A single admin account without MFA fails the entire assessment",
        ],
      },
      {
        key: "ua_cloud_user_mfa",
        title: "A7.17 [AUTO-FAIL] — MFA applied to all cloud service user accounts",
        description:
          "MFA is enabled and enforced on every user account (not just admins) on every in-scope cloud service. A 'No' answer is an automatic assessment failure.",
        guidance: [
          "Enable MFA for all users in Microsoft 365, Google Workspace, and every other cloud platform in scope",
          "Use security defaults or Conditional Access to enforce MFA at sign-in",
          "Include shared mailboxes and service accounts that have interactive sign-in enabled",
          "A single user account without MFA fails the entire assessment",
        ],
      },
    ],
  },
  {
    id: "malware_protection",
    title: "A8 — Malware Protection",
    controls: [
      {
        key: "mp_coverage",
        title: "A8.1 — All devices protected by anti-malware software (Option A) and/or application allowlisting (Option B)",
        description:
          "Every in-scope device is protected from malware by at least one of: anti-malware software (required for Windows and macOS); application allowlisting. Both methods may be used together.",
        guidance: [
          "Option A (Anti-malware): Deploy approved anti-malware on all Windows and macOS devices; extend to other platforms where supported",
          "Option B (Allowlisting): Implement application allowlisting on all devices so only approved, signed applications can run",
          "Confirm coverage extends to servers, not just end-user devices",
          "Document which option(s) are in use — this determines which of A8.2–A8.5 apply",
        ],
      },
      {
        key: "mp_am_updated",
        title: "A8.2 — Anti-malware kept up to date and configured to prevent malware running (if using Option A)",
        description:
          "Anti-malware software is updated in line with vendor guidelines and is configured to prevent malware from running, prevent execution of malicious code, and block connections to malicious websites.",
        guidance: [
          "Configure anti-malware to update its definitions automatically in line with vendor recommendations (usually daily or more frequently)",
          "Ensure the engine itself is kept up to date — not just definitions",
          "Confirm real-time/on-access scanning is enabled so threats are caught when files are opened or executed",
          "Monitor for update failures via a central management console",
        ],
      },
      {
        key: "mp_web_scan",
        title: "A8.3 — Anti-malware scans web pages and warns about malicious websites (if using Option A)",
        description:
          "Anti-malware includes web/browser protection that scans pages users visit and warns them before loading known malicious content.",
        guidance: [
          "Enable the web protection or browser scanning component of the anti-malware solution",
          "Confirm it warns users before they access known phishing, malware distribution, or C2 sites",
          "Apply to all devices, including remote workers' laptops",
          "Test the web scanning functionality by attempting to visit a known test/safe malicious URL (e.g. EICAR test site)",
        ],
      },
      {
        key: "mp_app_signing",
        title: "A8.4 — Users restricted from installing unsigned or unapproved applications (if using Option B)",
        description:
          "Application allowlisting prevents users from installing or running applications that are not approved and digitally signed by a trusted source.",
        guidance: [
          "Configure the device so only applications signed by a trusted publisher can be installed",
          "Use AppLocker, Windows Defender Application Control, or equivalent MDM policy to enforce this",
          "Prevent standard users from bypassing signing requirements or adding their own trusted certificates",
          "Test by attempting to install an unsigned application — it should be blocked",
        ],
      },
      {
        key: "mp_approved_apps",
        title: "A8.5 — Approved application list maintained and actively managed (if using Option B)",
        description:
          "A current list of approved applications is maintained and used to control which applications are permitted to run on in-scope devices.",
        guidance: [
          "Maintain a formal approved-application list with application names, publishers, and versions",
          "Review and update the list whenever new software is needed or existing software is retired",
          "Ensure the allowlist is enforced technically — not just a document list",
          "Audit installed applications against the approved list at least quarterly",
        ],
      },
    ],
  },
];
